import React, { useState, useEffect } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { DollarSign, Percent, Calendar, Car, Info, CreditCard, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';

const CarLoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [downPayment, setDownPayment] = useState('0');
  const [balloonPayment, setBalloonPayment] = useState('0');
  const [result, setResult] = useState<{
    monthlyPayment: number;
    totalInterest: number;
    totalCost: number;
    amortizationSchedule: Array<{
      month: number;
      payment: number;
      principal: number;
      interest: number;
      balance: number;
    }>;
  } | null>(null);

  const calculateLoan = () => {
    // Convert inputs to numbers
    const principal = parseFloat(loanAmount) - parseFloat(downPayment || '0');
    const rate = parseFloat(interestRate) / 100 / 12; // Monthly interest rate
    const term = parseFloat(loanTerm) * 12; // Term in months
    const balloon = parseFloat(balloonPayment || '0');

    if (principal > 0 && rate > 0 && term > 0) {
      // Calculate monthly payment using formula: P × (r × (1 + r)^n) / ((1 + r)^n - 1) - B × r / ((1 + r)^n - 1)
      // Where B is the balloon payment
      const monthlyPayment = 
        (principal * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1)) - 
        (balloon * rate / (Math.pow(1 + rate, term) - 1));
      
      // Calculate amortization schedule
      let balance = principal;
      let totalInterest = 0;
      const amortizationSchedule = [];

      for (let month = 1; month <= term; month++) {
        const interestPayment = balance * rate;
        let principalPayment = monthlyPayment - interestPayment;
        
        // For the last payment, add the balloon amount
        if (month === term) {
          principalPayment += balloon;
        }
        
        balance -= principalPayment;
        totalInterest += interestPayment;
        
        amortizationSchedule.push({
          month,
          payment: month === term ? monthlyPayment + balloon : monthlyPayment,
          principal: principalPayment,
          interest: interestPayment,
          balance: Math.max(0, balance)
        });
      }

      setResult({
        monthlyPayment,
        totalInterest,
        totalCost: principal + totalInterest,
        amortizationSchedule
      });
    }
  };

  // Generate chart data (yearly summary)
  const getChartData = () => {
    if (!result) return [];
    
    const yearlyData = [];
    const years = Math.ceil(result.amortizationSchedule.length / 12);
    
    for (let year = 1; year <= years; year++) {
      const yearStart = (year - 1) * 12;
      const yearEnd = Math.min(year * 12, result.amortizationSchedule.length) - 1;
      const startBalance = year === 1 ? parseFloat(loanAmount) - parseFloat(downPayment || '0') : result.amortizationSchedule[yearStart - 1].balance;
      const endBalance = result.amortizationSchedule[yearEnd].balance;
      
      yearlyData.push({
        name: `Year ${year}`,
        principal: startBalance - endBalance,
        interest: result.amortizationSchedule
          .slice(yearStart, yearEnd + 1)
          .reduce((sum, month) => sum + month.interest, 0),
        balance: endBalance
      });
    }
    
    return yearlyData;
  };

  // Calculate comparison rates based on typical Australian fees
  const getComparisonRate = () => {
    if (!result || !interestRate) return null;
    
    const rate = parseFloat(interestRate);
    // Add typical Australian loan establishment fee ($400) and monthly fees ($10/month)
    const loanAmt = parseFloat(loanAmount) || 30000;
    const loanTermYears = parseFloat(loanTerm) || 5;
    
    const establishmentFee = 400;
    const monthlyFee = 10;
    const totalFees = establishmentFee + (monthlyFee * loanTermYears * 12);
    
    // Approximate comparison rate calculation (simplified)
    const comparisonRate = rate + ((totalFees / loanAmt) * 100 / loanTermYears);
    
    return comparisonRate.toFixed(2);
  };

  return (
    <>
      <SEOHead
        title="Australian Car Loan Repayment Calculator | Calculate Monthly Repayments"
        description="Calculate your monthly Australian car loan repayments with our free car finance calculator. Compare options with balloon payments, variable loan terms and see your complete repayment schedule."
        keywords="australian car loan calculator, car repayment calculator australia, car finance australia, secured car loan calculator, car loan comparison, balloon payment calculator, novated lease calculator, car finance rates australia"
      />
      <Helmet>
        <title>Australian Car Loan Repayment Calculator | Calculate Monthly Repayments</title>
        <meta name="description" content="Calculate your monthly Australian car loan repayments with our free car finance calculator. Compare options with balloon payments, variable loan terms and see your complete repayment schedule." />
        <meta name="keywords" content="australian car loan calculator, car repayment calculator australia, car finance australia, secured car loan calculator, car loan comparison, balloon payment calculator, novated lease calculator, car finance rates australia" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Australian Car Loan Calculator",
              "description": "Calculate your monthly car loan repayments for Australian vehicles with our free calculator. Compare different interest rates and loan terms.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active",
              "financialProductCategory": "CreditCard"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Australian Car Loan Repayment Calculator"
        description="Calculate your monthly car loan repayments, total interest, and view your amortization schedule based on Australian loan terms."
        icon={Car}
        result={result ? `Monthly Repayment: $${result.monthlyPayment.toFixed(2)}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                This calculator is tailored for Australian car loans. Enter your loan details to estimate repayments 
                with typical Australian interest rates and terms.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Loan Amount"
              value={loanAmount}
              onChange={setLoanAmount}
              unit="$"
              placeholder="e.g. 30000"
            />
            <Input
              label="Down Payment"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 5000"
            />
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 5.25"
            />
            <Input
              label="Loan Term"
              value={loanTerm}
              onChange={setLoanTerm}
              unit="years"
              placeholder="e.g. 5"
            />
            <Input
              label="Balloon Payment (Optional)"
              value={balloonPayment}
              onChange={setBalloonPayment}
              unit="$"
              placeholder="e.g. 5000"
            />
          </div>

          <button
            onClick={calculateLoan}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
          >
            Calculate Repayments
          </button>

          {result !== null && (
            <div className="mt-6 space-y-6">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Loan Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Monthly Repayment</p>
                    <p className="text-xl font-bold text-blue-800">${result.monthlyPayment.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Total Interest</p>
                    <p className="text-xl font-bold text-blue-800">${result.totalInterest.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Total Loan Cost</p>
                    <p className="text-xl font-bold text-blue-800">${result.totalCost.toFixed(2)}</p>
                  </div>
                </div>
                
                {interestRate && (
                  <div className="mt-4 flex justify-between items-center bg-gray-50 p-3 rounded-md">
                    <div className="flex items-center">
                      <Percent className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm">Estimated Comparison Rate:</span>
                    </div>
                    <span className="text-sm font-semibold">{getComparisonRate()}% p.a.</span>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Loan Breakdown</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={getChartData()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => typeof value === 'number' ? `$${value.toFixed(2)}` : value} />
                      <Legend />
                      <Line type="monotone" dataKey="principal" stroke="#3b82f6" name="Principal Paid" />
                      <Line type="monotone" dataKey="interest" stroke="#ef4444" name="Interest Paid" />
                      <Line type="monotone" dataKey="balance" stroke="#10b981" name="Remaining Balance" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Amortization Schedule</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Balance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.amortizationSchedule.map((month) => (
                        <tr key={month.month}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.month}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${month.payment.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${month.principal.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${month.interest.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${month.balance.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gray-50 p-6 rounded-md">
            <h3 className="text-lg font-semibold mb-4">Australian Car Loan Options</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-4">
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <CreditCard className="h-4 w-4 text-blue-600 mr-2" /> 
                  Secured Car Loans
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Uses your vehicle as security, offering lower interest rates from 4.99% p.a.
                </p>
                <p className="text-xs text-gray-500">
                  Most common option for new and near-new vehicles in Australia
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <CreditCard className="h-4 w-4 text-blue-600 mr-2" /> 
                  Unsecured Car Loans
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  No security required, but higher interest rates starting from 8.99% p.a.
                </p>
                <p className="text-xs text-gray-500">
                  Suitable for older vehicles or when more flexibility is needed
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <TrendingDown className="h-4 w-4 text-blue-600 mr-2" /> 
                  Balloon Payment Options
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Reduce monthly repayments by deferring a portion of the loan to the end of the term.
                </p>
                <p className="text-xs text-gray-500">
                  Popular with Australian business buyers for tax benefits
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <CreditCard className="h-4 w-4 text-blue-600 mr-2" /> 
                  Novated Leases
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Salary packaging arrangement with potential tax benefits for Australian employees.
                </p>
                <p className="text-xs text-gray-500">
                  Employer makes repayments from your pre-tax salary
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Note: This calculator provides estimates based on the information you provide. Actual loan terms may vary based on lender policies, credit score, and vehicle details. We recommend comparing offers from multiple Australian lenders.
              </p>
            </div>
          </div>

          {/* Preset repayment examples — answers the most-searched "$X car loan repayments" queries */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">Common car loan repayment examples</h2>
            <p className="text-sm text-gray-600 mb-4">
              Indicative principal-and-interest repayments at example rates over a 5-year (60-month) term. These are
              illustrative only — enter your own figures above for an exact estimate.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">Loan amount</th>
                    <th className="border px-3 py-2">5% p.a.</th>
                    <th className="border px-3 py-2">7% p.a.</th>
                    <th className="border px-3 py-2">9% p.a.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-3 py-2 font-medium">$30,000 over 5 years</td>
                    <td className="border px-3 py-2 text-center">~$566/mo</td>
                    <td className="border px-3 py-2 text-center">~$594/mo</td>
                    <td className="border px-3 py-2 text-center">~$623/mo</td>
                  </tr>
                  <tr>
                    <td className="border px-3 py-2 font-medium">$50,000 over 5 years</td>
                    <td className="border px-3 py-2 text-center">~$944/mo</td>
                    <td className="border px-3 py-2 text-center">~$990/mo</td>
                    <td className="border px-3 py-2 text-center">~$1,038/mo</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 space-y-3 text-sm text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-800">What are the repayments on a $30,000 car loan?</h3>
                <p>
                  A $30,000 car loan at an example rate of 7% p.a. over 5 years is about $594 per month (roughly $5,642 in
                  total interest). Over 7 years it drops to about $453 per month, but you pay more interest overall.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">What are the repayments on a $50,000 car loan?</h3>
                <p>
                  A $50,000 car loan at an example rate of 7% p.a. over 5 years is about $990 per month. Over 7 years it's
                  about $755 per month. Adjust the amount, rate and term above for your exact repayment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default CarLoanCalculator; 